import { Brick, registerBrick, BrickContext, QueryResult, ErrorFlow } from 'olympe';
import { getLogger } from '@olympeio/core';

export default class MapListWithObject extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {!Object} object
     * @param {Array | QueryResult} list
     * @param {Brick} iterator
     * @param {function()} forwardEvent
     * @param {function(ErrorFlow)} setErrorFlow
     * @param {function(*)} setProcessed
     */
    update($, [object, list, iterator], [forwardEvent, setErrorFlow, setProcessed]) {
        const logger =  getLogger('MapListWithObject');

        // Check inputs
        if (!list || !iterator) {
            logger.warn('Nothing to do: list or iterator is null or undefined.');
            forwardEvent();
            return;
        }

        // Get input as array
        const array = Array.isArray(list) ? list : list.toArray();

        // Run lambda
        this.process($, object, array, iterator).then(result => {
            // Error flow is triggered
            if (result instanceof ErrorFlow) {
                setErrorFlow(result);
            }

            // Control flow is triggered
            else {
                setProcessed(result);
                forwardEvent();
            }
        }).catch((error) => {
            logger.error(error.message);
        });
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {!Object} object
     * @param {!Array} array
     * @param {!Brick} iterator
     * @return {!Promise<!Array<*> | ErrorFlow>}
     */
    async process($, object, array, iterator) {
        // Retrieve I/O
        const [inputControlFlow, inputObject, inputItem, inputRank, inputList] = iterator.getInputs();
        const [outputControlFlow, outputErrorFlow, outputItem] = iterator.getOutputs();

        const map = (object, item, rank, array) => {
            const mapper$ = $.runner(iterator)
                .set(inputObject, object)
                .set(inputItem, item)
                .set(inputRank, rank)
                .set(inputList, array)
                .trigger(inputControlFlow);

            // Wait for either the control flow or the error flow
            return Promise.any([
                mapper$.waitFor(outputControlFlow),
                mapper$.waitFor(outputErrorFlow)
            ]).then((outputFlow) => {
                // Error flow is triggered
                if (outputFlow instanceof ErrorFlow) {
                    return outputFlow;
                }
                // Return processed value
                return mapper$.get(outputItem);
            }).finally(() => {
                mapper$.destroy();
            });
        };

        const mappedArray = [];
        for (let i = 0, l = array.length; i < l; i++) {
            if ($.isDestroyed()) {
                throw new Error('MapListWithObject context has been destroyed');
            }
            const result = await map(object, array[i], i, array);
            if (result instanceof ErrorFlow) {
                return result;
            }
            mappedArray.push(result);
        }
        return mappedArray;
    }
}

registerBrick('01882926fa5180c2739c', MapListWithObject);
